import React, { useCallback, useState } from 'react';
import { Button, Column, GenericConfirmationModal, Row } from '@ydistri/ds';
import { COLORS } from '@ydistri/theme';
import { addToast } from '@ydistri/utils';
import { Card, theme } from 'antd';
import AsoSourceScope from './AsoSourceScope';
import AsoTargetScope from './AsoTargetScope';
import { useCreateAsoScopeTargetMutation, useDeleteAsoScopeMutation } from '../apiAso';
import AsoLayers from '../AsoLayers/AsoLayers';
import { TwoColumnContainer } from '../components/TwoColumnContainer';
import { TbPlus } from 'react-icons/tb';
import { AsoScenarioScopeResponse } from '@ydistri/api-sdk';
import { useAsoScenarioStatus } from '../hooks/useAsoScenarioStatus';
import AsoTooltip from '../AsoTooltip';
import { AsoValidationIssueMessages } from '../validation/AsoValidationIssueMessages';
import {
  getAsoValidationIssuesForScopeCard,
  getAsoValidationIssuesForScopeSource,
  getAsoValidationIssuesForScopeTarget,
} from '../validation/asoValidation';
import type { AsoValidationIssue } from '../validation/asoValidationTypes';
import { getRequestErrorMessage } from '../../../lib/utils/getRequestErrorMessage';
import { useAppDispatch } from '../../../store';

export interface AsoScopeProps {
  asoScenarioId: number;
  scopeNumber: number;
  scope: AsoScenarioScopeResponse;
  validationIssues: readonly AsoValidationIssue[];
}

const AsoScope: React.FC<AsoScopeProps> = ({
  asoScenarioId,
  scopeNumber,
  scope,
  validationIssues,
}) => {
  const [createTarget, { isLoading }] = useCreateAsoScopeTargetMutation();
  const [deleteScope, { isLoading: isDeletingScope }] = useDeleteAsoScopeMutation();
  const [showConfirm, setShowConfirm] = useState(false);
  const { isLocked } = useAsoScenarioStatus();
  const dispatch = useAppDispatch();
  const { token } = theme.useToken();

  const onNewTargetPriority = useCallback(async () => {
    if (isLocked) return;

    try {
      await createTarget({ asoScenarioId, scopeId: scope.id }).unwrap();
    } catch (error) {
      dispatch(
        addToast({
          isError: true,
          message: getRequestErrorMessage(error, 'The target priority could not be added.'),
        }),
      );
    }
  }, [asoScenarioId, createTarget, dispatch, isLocked, scope.id]);

  const onDeleteScope = useCallback(async () => {
    if (isLocked) return;

    try {
      await deleteScope({
        asoScenarioId,
        reusableScopeIds: [
          scope.source.scopeId,
          ...scope.targets.map(target => target.scopeId),
        ].filter((scopeId): scopeId is number => scopeId !== null && scopeId !== undefined),
        scopeId: scope.id,
      }).unwrap();
      setShowConfirm(false);
    } catch (error) {
      dispatch(
        addToast({
          isError: true,
          message: getRequestErrorMessage(error, 'The ASO scope could not be deleted.'),
        }),
      );
    }
  }, [
    asoScenarioId,
    deleteScope,
    dispatch,
    isLocked,
    scope.id,
    scope.source.scopeId,
    scope.targets,
  ]);

  const scopeCardIssues = getAsoValidationIssuesForScopeCard(validationIssues, scope.id);
  const sourceIssues = getAsoValidationIssuesForScopeSource(
    validationIssues,
    scope.id,
    scope.source.id,
  );

  return (
    <Card
      styles={{
        cover: {
          padding: '1rem',
          background: COLORS.GREY_LIGHT,
          border: `1px solid ${token.colorBorderSecondary}`,
        },
      }}
      title={
        <Row $alignItems="center" $gap="0.25rem">
          <span>{`ASO scope ${scopeNumber}`}</span>
          <AsoTooltip tooltipKey="scope" />
        </Row>
      }
      extra={
        <Row $alignItems="center" $gap="0.25rem">
          <Button
            $colorVariant="ghostDanger"
            onClick={() => setShowConfirm(true)}
            disabled={isLoading || isDeletingScope || isLocked}
          >
            Delete ASO scope
          </Button>
        </Row>
      }
      cover={
        <TwoColumnContainer>
          <AsoLayers asoScenarioId={asoScenarioId} scopeId={scope.id} variant="scope" />
        </TwoColumnContainer>
      }
    >
      <Column $gap="0.75rem">
        <AsoValidationIssueMessages issues={scopeCardIssues} />
        <TwoColumnContainer>
          <AsoSourceScope
            asoScenarioId={asoScenarioId}
            scopeId={scope.id}
            source={scope.source}
            validationIssues={sourceIssues}
          />
          <Column $gap="0.5rem">
            {scope.targets.map(t => (
              <AsoTargetScope
                key={t.id}
                asoScenarioId={asoScenarioId}
                scopeId={scope.id}
                target={t}
                validationIssues={getAsoValidationIssuesForScopeTarget(
                  validationIssues,
                  scope.id,
                  t.id,
                )}
              />
            ))}
            <Row $justifyContent="end" $alignItems="center" $gap="0.25rem">
              <Button
                disabled={isLoading || isDeletingScope || isLocked}
                loading={isLoading}
                onClick={() => void onNewTargetPriority()}
              >
                <TbPlus /> Add target priority
              </Button>
            </Row>
          </Column>
        </TwoColumnContainer>
      </Column>
      {showConfirm && (
        <GenericConfirmationModal
          title="Delete ASO scope"
          message="You are about to delete this ASO scope with all its targets."
          confirmationButtonLabel="Delete ASO scope"
          running={isDeletingScope}
          onConfirmed={() => void onDeleteScope()}
          onCanceled={() => setShowConfirm(false)}
        />
      )}
    </Card>
  );
};

export default AsoScope;
